import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';

import { apiErrorReason } from '@/lib/api';
import { useApi } from '@/lib/api-context';

// Agregar o quitar una cláusula de norma de un documento (multinorma). Cada cambio
// refresca la ficha y la Lista Maestra (columna y filtro por norma), también tras un
// rechazo: otro usuario pudo cambiar la clasificación al mismo tiempo.

const BASE = '/api/v1/quality/master-list';

function useRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['dossier'] }),
      qc.invalidateQueries({ queryKey: ['master-list'] }),
    ]);
  };
}

/** Agrega la cláusula `normId` al documento. */
/** Rechazos que indican que el catálogo de normas en pantalla quedó viejo. */
const STALE_CATALOG = new Set(['inactive_norm', 'cross_company_norm']);

export function useClassify(entryId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (normId) => {
      await api.post(`${BASE}/${entryId}/norms`, { normId });
    },
    onError: async (err) => {
      // La cláusula se desactivó o ya no existe: el selector deja de ofrecerla.
      const reason = apiErrorReason(err);
      if (reason !== undefined && STALE_CATALOG.has(reason)) {
        await qc.invalidateQueries({ queryKey: ['quality-catalogs'] });
      }
    },
    onSettled: refresh,
  });
}

/** Quita la cláusula `normId` del documento. */
export function useUnclassify(entryId: string): UseMutationResult<void, Error, string> {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (normId) => {
      await api.delete(`${BASE}/${entryId}/norms/${normId}`);
    },
    onSettled: refresh,
  });
}
